<template>
  <article
    class="v2-event-row v2-card v2-card--clickable"
    :class="{ 'v2-event-row--live': isLive, 'v2-event-row--past': isPast }"
    @click="onClick"
  >
    <div class="v2-event-row__media">
      <img
        v-if="thumbnail"
        :src="thumbnail"
        :alt="title"
        class="v2-event-row__image"
        loading="lazy"
      />
      <img
        v-else
        :src="placeholderImage"
        :alt="`Placeholder para ${title}`"
        class="v2-event-row__image v2-event-row__image--placeholder"
        loading="lazy"
      />
    </div>

    <div class="v2-event-row__body">
      <div class="v2-event-row__date" aria-hidden="true">
        <span class="v2-event-row__date-day">{{ dateDay }}</span>
        <span class="v2-event-row__date-month">{{ dateMonth }}</span>
      </div>

      <div class="v2-event-row__main">
        <div class="v2-event-row__head">
          <span v-if="category" class="v2-event-row__category">
            <v-icon size="12">{{ categoryIcon }}</v-icon>
            {{ category }}
          </span>
          <span
            v-if="isLive"
            class="v2-event-row__badge v2-event-row__badge--live"
          >
            <span class="v2-event-row__pulse" />
            En vivo
          </span>
          <span
            v-else-if="isPast"
            class="v2-event-row__badge v2-event-row__badge--past"
          >
            Finalizado
          </span>
        </div>

        <h3 class="v2-event-row__title">{{ title }}</h3>

        <p v-if="timeLabel" class="v2-event-row__time">
          <v-icon size="14">mdi-clock-outline</v-icon>
          {{ timeLabel }}
          <span v-if="typeLabel" class="v2-event-row__sep">·</span>
          <v-icon v-if="typeLabel" size="14">{{ typeIcon }}</v-icon>
          <span v-if="typeLabel">{{ typeLabel }}</span>
          <template v-if="location">
            <span class="v2-event-row__sep">·</span>
            <v-icon size="14">mdi-map-marker-outline</v-icon>
            <span>{{ location }}</span>
          </template>
        </p>

        <p v-if="description" class="v2-event-row__desc">{{ description }}</p>
      </div>

      <div class="v2-event-row__action" aria-hidden="true">
        <v-icon size="20">mdi-chevron-right</v-icon>
      </div>
    </div>
  </article>
</template>

<script>
import {
  getEventId,
  getEventTitle,
  getEventDescription,
  getEventThumbnail,
  getEventCategory,
  getEventType,
  getEventStartMoment,
  getEventEndMoment,
  getEventLocation,
  formatEventTime,
  isLive,
  resolveEventTypeLabel,
  resolveEventTypeIcon,
  resolveEventDetailRoute,
} from "@/views/memberDashboardV2/events/eventsHelpers";
import placeholderImage from "@/assets/images/shared/Events_Placeholder.png";
import moment from "moment-timezone";

export default {
  name: "UpcomingEventHorizontal",
  props: {
    event: { type: Object, required: true },
  },
  data() {
    return {
      placeholderImage,
    };
  },
  computed: {
    id() {
      return getEventId(this.event);
    },
    title() {
      return getEventTitle(this.event);
    },
    description() {
      return getEventDescription(this.event);
    },
    thumbnail() {
      return getEventThumbnail(this.event);
    },
    category() {
      return getEventCategory(this.event);
    },
    typeLabel() {
      return resolveEventTypeLabel(getEventType(this.event));
    },
    typeIcon() {
      return resolveEventTypeIcon(getEventType(this.event));
    },
    location() {
      return getEventLocation(this.event);
    },
    dateDay() {
      const start = getEventStartMoment(this.event);
      return start ? start.format("DD") : "";
    },
    dateMonth() {
      const start = getEventStartMoment(this.event);
      return start ? start.format("MMM").toUpperCase() : "";
    },
    timeLabel() {
      return formatEventTime(this.event);
    },
    isLive() {
      return isLive(this.event);
    },
    isPast() {
      const start = getEventStartMoment(this.event);
      if (!start) return false;
      const end = getEventEndMoment(this.event);
      return end ? end.isBefore(moment()) : start.isBefore(moment(), "day");
    },
    categoryIcon() {
      const cat = (this.category || "").toLowerCase();
      if (cat.includes("taller") || cat.includes("workshop")) return "mdi-hammer-wrench";
      if (cat.includes("networking") || cat.includes("red")) return "mdi-account-group";
      if (cat.includes("conferencia") || cat.includes("conference")) return "mdi-presentation";
      if (cat.includes("social")) return "mdi-glass-cocktail";
      if (cat.includes("formacion") || cat.includes("training")) return "mdi-school";
      return "mdi-tag-outline";
    },
    detailRoute() {
      return resolveEventDetailRoute(this.event);
    },
  },
  methods: {
    onClick() {
      if (!this.id) return;
      this.$router.push(this.detailRoute);
    },
  },
};
</script>

<style scoped>
.v2-event-row {
  display: flex;
  align-items: stretch;
  gap: 0;
  overflow: hidden;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: transform var(--transition-base), box-shadow var(--transition-base);
  text-align: left;
  width: 100%;
}

.v2-event-row:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-card-hover);
}

.v2-event-row:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}

.v2-event-row__media {
  position: relative;
  width: 140px;
  min-width: 140px;
  background-color: var(--color-neutral-100);
  overflow: hidden;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .v2-event-row__media {
    width: 180px;
    min-width: 180px;
  }
}

.v2-event-row__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform var(--transition-base);
}

.v2-event-row__image--placeholder {
  object-fit: cover;
}

.v2-event-row:hover .v2-event-row__image {
  transform: scale(1.04);
}

.v2-event-row__body {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4);
  flex: 1 1 auto;
  min-width: 0;
}

.v2-event-row__date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: var(--space-2) var(--space-3);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  border-radius: var(--radius-md);
  text-align: center;
  flex-shrink: 0;
  align-self: center;
}

.v2-event-row__date-day {
  font-family: var(--font-headline);
  font-size: var(--fs-display-md);
  font-weight: var(--fw-bold);
  line-height: 1;
}

.v2-event-row__date-month {
  font-family: var(--font-body);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  margin-top: var(--space-1);
}

.v2-event-row__main {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  flex: 1 1 auto;
}

.v2-event-row__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.v2-event-row__category {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  background-color: var(--color-amber-soft-2);
  color: var(--color-amber-on);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
}

.v2-event-row__badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
  line-height: 1;
}

.v2-event-row__badge--live {
  background-color: var(--color-error);
  color: var(--color-error-on);
}

.v2-event-row__badge--past {
  background-color: var(--color-neutral-700);
  color: var(--color-text-inverse);
}

.v2-event-row__pulse {
  width: 6px;
  height: 6px;
  border-radius: var(--radius-full);
  background-color: currentColor;
  animation: v2-event-row-pulse 1.2s ease-in-out infinite;
}

@keyframes v2-event-row-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.v2-event-row__title {
  font-family: var(--font-headline);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  line-height: var(--lh-snug);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.v2-event-row__time {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  line-height: var(--lh-snug);
}

.v2-event-row__time :first-child,
.v2-event-row__time :nth-child(2) {
  flex-shrink: 0;
}

.v2-event-row__sep {
  margin: 0 var(--space-1);
  color: var(--color-text-subtle);
}

.v2-event-row__desc {
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
  line-height: var(--lh-normal);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.v2-event-row__action {
  display: none;
  align-items: center;
  justify-content: center;
  color: var(--color-text-subtle);
  flex-shrink: 0;
  align-self: center;
}

@media (min-width: 768px) {
  .v2-event-row__action {
    display: flex;
  }
}

.v2-event-row--past {
  opacity: 0.7;
}

.v2-event-row--past:hover {
  transform: none;
}
</style>
